import { MobileNumberField } from '@components/ui/MobileNumberField';
import { useCreateMainCentre } from '@features/settings';
import { CANCEL_BTN, FIELD, LABEL, SAVE_BTN } from '@features/settings/styles';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useState } from 'react';

interface MainCentreFormState {
  name: string;
  code: string;
  instituteName: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  phoneNumber: string;
  email: string;
  gstin: string;
  cin: string;
  sacCode: string;
}

const EMPTY_FORM: MainCentreFormState = {
  name: '', code: '', instituteName: '',
  addressLine1: '', addressLine2: '', addressLine3: '',
  phoneNumber: '', email: '', gstin: '', cin: '', sacCode: '',
};

// Everything printed on a receipt is collected here, because a main centre is
// one registered entity: its centres share the name, address, contact and GSTIN
// and supply nothing of their own.
const FIELDS: { key: keyof MainCentreFormState; label: string; required?: boolean; placeholder?: string; full?: boolean }[] = [
  { key: 'name',          label: 'Main Centre Name', required: true, placeholder: 'e.g. Kurukshetra' },
  { key: 'code',          label: 'Main Centre Code', required: true, placeholder: 'e.g. KKR' },
  { key: 'instituteName', label: 'Institute Name', required: true, placeholder: 'Legal name shown on receipts', full: true },
  { key: 'addressLine1',  label: 'Address Line 1', required: true },
  { key: 'addressLine2',  label: 'Address Line 2 (Optional)' },
  { key: 'addressLine3',  label: 'Address Line 3 (Optional)', placeholder: 'City, State, PIN' },
  { key: 'phoneNumber',   label: 'Phone Number', required: true, placeholder: '10-digit phone number' },
  { key: 'email',         label: 'Email Address', required: true, placeholder: 'e.g. info@shriji.edu.in' },
  { key: 'gstin',         label: 'GSTIN', required: true, placeholder: 'Shared by every centre here' },
  { key: 'cin',           label: 'CIN Number (Optional)' },
  { key: 'sacCode',       label: 'SAC Code (Optional)' },
];

const REQUIRED_KEYS: [keyof MainCentreFormState, string][] = FIELDS
  .filter((f) => f.required)
  .map((f) => [f.key, f.label]);

interface AddMainCentreDialogProps {
  open: boolean;
  onClose: () => void;
}

type FieldErrors = Partial<Record<keyof MainCentreFormState, string>>;

/**
 * Nested "New Main Centre" dialog, opened from ManageMainCentresDialog's header.
 * A main centre is a city that owns centres, and it carries the whole receipt
 * identity — so it is collected up front here rather than per centre.
 */
export const AddMainCentreDialog = ({ open, onClose }: AddMainCentreDialogProps) => {
  const [form, setForm] = useState<MainCentreFormState>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const createMainCentre = useCreateMainCentre();

  const handleClose = () => {
    onClose();
    setForm(EMPTY_FORM);
    setFieldErrors({});
  };

  const setField = (key: keyof MainCentreFormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleSubmit = () => {
    const errors: FieldErrors = {};
    REQUIRED_KEYS.forEach(([key, label]) => {
      if (!form[key].trim()) errors[key] = `${label} is required`;
    });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    createMainCentre.mutate(
      {
        name: form.name.trim(),
        code: form.code.trim(),
        instituteName: form.instituteName.trim(),
        addressLine1: form.addressLine1.trim(),
        addressLine2: form.addressLine2.trim() || undefined,
        addressLine3: form.addressLine3.trim() || undefined,
        phoneNumber: form.phoneNumber.trim(),
        email: form.email.trim(),
        gstin: form.gstin.trim(),
        cin: form.cin.trim() || undefined,
        sacCode: form.sacCode.trim() || undefined,
      },
      { onSuccess: handleClose },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{ paper: { sx: { borderRadius: '16px', p: '8px', width: { xs: 'calc(100vw - 32px)', sm: 640 }, maxWidth: 'calc(100vw - 32px)' } } }}
    >
      <DialogTitle sx={(theme) => ({ fontSize: 16, fontWeight: 700, color: theme.palette.grey[900], pb: '4px' })}>
        Add New Main Centre
      </DialogTitle>
      <DialogContent>
        <Typography sx={(theme) => ({ fontSize: 12.5, color: theme.palette.grey[500], mb: '16px' })}>
          A main centre is a city holding one or more centres. Everything below is printed on the
          receipts of every centre you add under it — each centre supplies only its own name and code.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: '16px' }}>
          {FIELDS.map(({ key, label, required, placeholder, full }) => (
            <Box key={key} sx={full ? { gridColumn: { xs: 'auto', sm: '1 / -1' } } : undefined}>
              <Typography component="label" sx={LABEL}>
                {label}
                {required && <Box component="span" sx={(theme) => ({ color: theme.palette.error.main })}> *</Box>}
              </Typography>
              {key === 'phoneNumber' ? (
                <MobileNumberField
                  value={form[key]}
                  onChange={setField(key)}
                  fullWidth
                  placeholder={placeholder}
                  error={!!fieldErrors[key]}
                  helperText={fieldErrors[key]}
                  sx={FIELD}
                />
              ) : (
                <TextField
                  value={form[key]}
                  onChange={setField(key)}
                  fullWidth
                  placeholder={placeholder}
                  error={!!fieldErrors[key]}
                  helperText={fieldErrors[key]}
                  sx={FIELD}
                />
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: '20px', pb: '16px', gap: '8px' }}>
        <Button variant="outlined" sx={CANCEL_BTN} onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={createMainCentre.isPending} sx={SAVE_BTN}>
          {createMainCentre.isPending ? <CircularProgress size={16} color="inherit" /> : 'Add Main Centre'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
